import { compareDesc, differenceInDays, subMonths, subYears } from "date-fns"

class SubscriptionPlan {
  constructor(
    id,
    name,
    description,
    period,
    price,
    features,
    popular,
    savings
  ) {
    this.id = id
    this.name = name
    this.description = description
    this.period = period
    this.price = price
    this.features = features
    this.popular = popular
    this.savings = savings
  }

  get label() {
    return `${this.name} Plan`
  }

  get billingCycle() {
    return `${this.name} billing`
  }
}

const MONTHLY_PLAN = new SubscriptionPlan(
  "monthly",
  "Monthly",
  "Unlimited candidate sourcing with MLS plus workflow automation and priority success support.",
  "month",
  799,
  [
    "Unlimited candidate leads",
    "Advanced candidate management",
    "MLS + Marketplace integrations",
    "Workflow automations",
    "Priority success manager",
  ],
  false,
  0
)

const ANNUAL_PLAN_PRICE = 7999
const ANNUAL_PLAN_SAVINGS = MONTHLY_PLAN.price * 12 - ANNUAL_PLAN_PRICE

const ANNUAL_PLAN = new SubscriptionPlan(
  "annual",
  "Annual",
  "Everything in Monthly plus dedicated strategist, advanced analyitcs and early beta access.",
  "year",
  ANNUAL_PLAN_PRICE,
  [
    "Everything in Monthly plan",
    "Dedicated account strategist",
    "Done-for-you onboarding",
    "Early access to beta features",
    "Enterprise-grade analytics",
  ],
  true,
  ANNUAL_PLAN_SAVINGS
)

export const SUBSCRIPTION_PLANS = [MONTHLY_PLAN, ANNUAL_PLAN]

export const SUBSCRIPTION_STATUS = {
  trial: "trialing",
  active: "active",
  // Zoho paid subscriptions use "live"; Stripe uses "active"
  live: "live",
}

/** Stripe "active" and Zoho "live" both mean a paid/active plan. */
export function isActiveSubscriptionStatus(status) {
  return (
    status === SUBSCRIPTION_STATUS.active || status === SUBSCRIPTION_STATUS.live
  )
}

export class Subscription {
  constructor(
    id,
    planID,
    cardData,
    status,
    trialEndsOn,
    validUntil,
    freeCandidatesRemaining
  ) {
    this.id = id
    this.planID = planID
    this.cardData = cardData
    this.status = status
    this.trialEndsOn = trialEndsOn
    this.validUntil = validUntil
    this.freeCandidatesRemaining = freeCandidatesRemaining
    this.plan = null
    for (const plan of SUBSCRIPTION_PLANS) {
      if (plan.id === this.planID) {
        this.plan = plan
      }
    }
  }

  get isInTrial() {
    return this.status === SUBSCRIPTION_STATUS.trial
  }

  get isActive() {
    return isActiveSubscriptionStatus(this.status)
  }

  isPlanActive(plan: SubscriptionPlan) {
    return this.planID === plan.id && this.isActive
  }

  get usedCandidateCount() {
    return INITIAL_FREE_CANDIDATES - this.freeCandidatesRemaining
  }

  get isMonthly() {
    return this.planID === MONTHLY_PLAN.id
  }

  get statusDisplay() {
    return {
      [SUBSCRIPTION_STATUS.trial]: "In trial",
      [SUBSCRIPTION_STATUS.active]: "Active",
      [SUBSCRIPTION_STATUS.live]: "Active",
    }[this.status]
  }

  get trialEndsOnDate() {
    return new Date(this.trialEndsOn)
  }

  get validUntilDate() {
    return new Date(this.validUntil)
  }

  get startDate() {
    const validUntilDate = this.validUntilDate
    return this.isMonthly
      ? subMonths(validUntilDate, 1)
      : subYears(validUntilDate, 1)
  }

  get isCloseToExpiry() {
    return differenceInDays(this.validUntilDate, new Date()) <= 7
  }

  get trialFinished() {
    if (this.isActive) {
      return false
    }
    return (
      compareDesc(this.trialEndsOnDate, new Date()) === 1 ||
      this.freeCandidatesRemaining === 0
    )
  }

  static fromAPI(subscription) {
    return subscription
      ? new Subscription(
          subscription.id,
          subscription.plan,
          subscription.card_data,
          subscription.status,
          subscription.trial_ends_on,
          subscription.valid_until,
          subscription.free_candidates_remaining
        )
      : null
  }

  static getDefaultSubscription() {
    // can't store Subscription objects in Redux
    return {
      id: 1,
      planID: MONTHLY_PLAN.id,
      cardData: {},
      status: SUBSCRIPTION_STATUS.active,
      trialEndsOn: new Date().toISOString(),
      validUntil: new Date().toISOString(),
      freeCandidatesRemaining: INITIAL_FREE_CANDIDATES,
    }
  }

  static getDefault() {
    const defaults = Subscription.getDefaultSubscription()
    return new Subscription(
      defaults.id,
      defaults.planID,
      defaults.cardData,
      defaults.status,
      defaults.trialEndsOn,
      defaults.validUntil,
      defaults.freeCandidatesRemaining
    )
  }
}

export const INITIAL_FREE_CANDIDATES = 50

export const DISABLED_AFTER_TRIAL_ENDS_CLASS_NAME = "disabled-after-trial"
